import React, { useState, useEffect } from 'react';
import DashboardLayout from "../layouts/DashboardLayout";
import InfoModal from "../layouts/InfoModal";
import PaginatedTableContainer from "../containers/PaginatedTableContainer";
import { getAllReports, deleteReport } from "../../api/AdminApi";

const ReportManagementPage = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // 모달 관련 상태
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);

    const fetchReports = async (page = 0) => {
        setLoading(true);
        try {
            const response = await getAllReports(page, 20);
            setReports(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
        } catch (error) {
            console.error('신고 목록 조회 실패:', error);
            alert('신고 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = (report) => {
        setSelectedReport(report);
        setModalOpen(true);
    };

    const handleDeleteReport = async () => {
        const confirmed = window.confirm(`정말로 신고 ID ${selectedReport.id}를 삭제하시겠습니까? 이 동작은 되돌릴 수 없습니다.`);
        if (!confirmed) return;
        
        try {
            await deleteReport(selectedReport.id);
            alert('신고가 삭제되었습니다.');
            setModalOpen(false);
            fetchReports(currentPage);
        } catch (error) {
            console.error('신고 삭제 실패:', error);
            alert('신고 삭제에 실패했습니다.');
        }
    };

    useEffect(() => {
        fetchReports();
    }, []);

    const handlePageChange = (page) => {
        if (page >= 0 && page < totalPages) {
            setCurrentPage(page);
            fetchReports(page);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString('ko-KR');
    };

    // 테이블 컬럼 정의
    const columns = [
        { header: 'id', field: 'id', minWidth: 'min-w-12' },
        { header: 'reporterId', field: 'reporterId', minWidth: 'min-w-16' },
        { header: 'reportedId', field: 'reportedId', minWidth: 'min-w-16' },
        { header: 'meetingId', field: 'meetingId', minWidth: 'min-w-16' },
        { 
            header: 'content', 
            field: 'content', 
            minWidth: 'min-w-32',
            render: (report) => {
                if (!report.content) return '';
                return report.content.length > 30 ? `${report.content.substring(0, 30)}...` : report.content;
            }
        },
        { header: 'status', field: 'status', minWidth: 'min-w-20' },
        { 
            header: 'adminComment', 
            field: 'adminComment', 
            minWidth: 'min-w-20',
            render: (report) => report.adminComment || ''
        },
        { 
            header: 'createdAt', 
            field: 'createdAt', 
            minWidth: 'min-w-32',
            render: (report) => formatDateTime(report.createdAt)
        },
        { 
            header: 'updatedAt', 
            field: 'updatedAt', 
            minWidth: 'min-w-32',
            render: (report) => formatDateTime(report.updatedAt)
        }
    ];

    return (
        <DashboardLayout>
            <div className="p-3">
                <h1 className="text-lg font-bold mb-3">멤버 신고 관리</h1>

                {/* 상단 정보 */}
                <div className="mb-2 text-xs text-gray-600">
                    총 {totalElements}건 | {currentPage + 1}/{totalPages} 페이지
                </div>

                {/* 페이지네이션 테이블 */}
                <PaginatedTableContainer
                    columns={columns}
                    data={reports}
                    loading={loading}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onRowClick={handleRowClick}
                />

                {/* 신고 상세 모달 */}
                <InfoModal 
                    isOpen={modalOpen} 
                    onClose={() => setModalOpen(false)} 
                    title={`신고 정보 - ID ${selectedReport?.id || ''}`}
                    width="w-[600px]"
                >
                    {selectedReport ? (
                        <div className="text-xs space-y-4">
                            {/* 신고 정보 테이블 */}
                            <div>
                                <h3 className="font-bold mb-2 text-sm">신고 정보</h3>
                                <table className="w-full border border-gray-400">
                                    <tbody>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100 w-24">id</td><td className="border border-gray-400 px-2 py-1">{selectedReport.id}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">reporterId</td><td className="border border-gray-400 px-2 py-1">{selectedReport.reporterId}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">reportedId</td><td className="border border-gray-400 px-2 py-1">{selectedReport.reportedId}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">meetingId</td><td className="border border-gray-400 px-2 py-1">{selectedReport.meetingId}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">content</td><td className="border border-gray-400 px-2 py-1">{selectedReport.content || ''}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">status</td><td className="border border-gray-400 px-2 py-1">{selectedReport.status}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">adminComment</td><td className="border border-gray-400 px-2 py-1">{selectedReport.adminComment || ''}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">createdAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedReport.createdAt)}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">updatedAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedReport.updatedAt)}</td></tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* 신고 삭제 */}
                            <div>
                                <h3 className="font-bold mb-2 text-sm">신고 삭제</h3>
                                <button 
                                    onClick={handleDeleteReport}
                                    className="px-3 py-1 bg-red-200 border border-gray-400 hover:bg-red-300 text-red-800"
                                >
                                    신고 삭제
                                </button>
                            </div>
                        </div>
                    ) : null}
                </InfoModal>
            </div>
        </DashboardLayout>
    );
};

export default ReportManagementPage;
